import React, { useEffect, useState, useRef, lazy } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { BadgeDollarSignIcon, XIcon } from 'lucide-react';
type ProductOffer = {
  title: string;
  editionLabel: string;
  priceNumber: number;
  priceText: string;
  thumbnailUrl: string;
};
const FALLBACK_OFFER: ProductOffer = {
  title: 'Cambio Hormonal · El Método del 1%',
  editionLabel: 'Programa guiado de 30 días',
  priceNumber: 17,
  priceText: '$17 USD',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/ocgfBLBq8orXEV2J28pDho/principal_.webp'
};
export function OfferToast() {
  const [offer, setOffer] = useState<ProductOffer>(FALLBACK_OFFER);
  const [isVisible, setIsVisible] = useState(false);
  const reducedMotion = useReducedMotion();
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const nextTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showOfferRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    const catalog = scanProductCatalog();
    if (catalog.length > 0) {
      setOffer(selectOffer(catalog));
    }
    const clearTimers = () => {
      if (showTimerRef.current) clearTimeout(showTimerRef.current);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      if (nextTimerRef.current) clearTimeout(nextTimerRef.current);
    };
    const scheduleNext = () => {
      nextTimerRef.current = window.setTimeout(() => showOfferRef.current?.(), randomBetween(8000, 15000));
    };
    const hideOffer = () => {
      setIsVisible(false);
      scheduleNext();
    };
    const showOffer = () => {
      const updatedCatalog = scanProductCatalog();
      if (updatedCatalog.length > 0) {
        setOffer(selectOffer(updatedCatalog));
      }
      setIsVisible(true);
      hideTimerRef.current = window.setTimeout(hideOffer, 5000);
    };
    showOfferRef.current = showOffer;
    showTimerRef.current = window.setTimeout(showOffer, 3000);
    return () => {
      showOfferRef.current = null;
      clearTimers();
    };
  }, []);
  const handleDismiss = () => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    if (nextTimerRef.current) clearTimeout(nextTimerRef.current);
    setIsVisible(false);
    nextTimerRef.current = window.setTimeout(() => showOfferRef.current?.(), randomBetween(8000, 15000));
  };
  const scrollToPurchase = () => {
    const purchaseButton = document.getElementById('comenzar-transformacion');
    purchaseButton?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
    window.setTimeout(() => purchaseButton?.focus({
      preventScroll: true
    }), 450);
  };
  const transition = reducedMotion ? {
    duration: 0
  } : {
    duration: 0.28,
    ease: [0.22, 1, 0.36, 1] as const
  };
  return <div className="pointer-events-none fixed inset-x-0 bottom-4 z-[60] flex justify-center px-4 sm:bottom-6" data-id="element-120" data-mp="3viv637cylwsj">
      <AnimatePresence data-id="element-121" data-mp="3bzo98qppinju">
        {isVisible && <motion.aside role="status" aria-live="polite" initial={reducedMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 20
      }} animate={reducedMotion ? {
        opacity: 1
      } : {
        opacity: 1,
        y: 0
      }} exit={reducedMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 20
      }} transition={transition} className="pointer-events-auto w-full max-w-sm rounded-2xl border border-nude/80 bg-white/95 p-3 shadow-2xl shadow-tinta/20 backdrop-blur-sm" data-id="element-122" data-mp="36ztj4pdra1zu">
            <div className="flex items-start gap-3" data-id="element-123" data-mp="2bxhumb3ubwvx">
              <img src="https://cdn.magicpatterns.com/uploads/ocgfBLBq8orXEV2J28pDho/principal_.webp" alt="" className="h-14 w-14 shrink-0 rounded-xl object-cover" loading="lazy" data-id="element-124" data-mp="2bvyauaiz73pe" />
              <div className="min-w-0 flex-1 pr-1" data-id="element-125" data-mp="1o1401xs16blp">
                <span className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-[0.14em] text-rosa-dark" data-id="element-126" data-mp="1o2f2nu9qpdb8">
                  <BadgeDollarSignIcon className="h-3.5 w-3.5" strokeWidth={2} data-id="element-127" data-mp="1xy0a47xkv5sd" />
                  Oferta de lanzamiento
                </span>
                <p className="mt-1 truncate font-serif text-lg font-semibold leading-tight text-tinta" data-id="element-128" data-mp="39e6qzpa0u6xx">
                  Cambio Hormonal · El Método del 1%
                </p>
                <p className="mt-0.5 truncate text-sm text-tinta/70" data-id="element-129" data-mp="11rf2qhzq4qus">
                  {offer.editionLabel}
                </p>
                <button type="button" onClick={scrollToPurchase} className="mt-2 text-sm font-medium text-rosa-dark underline underline-offset-4 transition-colors hover:text-tinta focus:outline-none focus-visible:ring-2 focus-visible:ring-rosa/40 rounded" data-id="element-130" data-mp="xr4lg96hee0m">
                  {offer.priceText} · Acceso inmediato
                </button>
              </div>
              <button type="button" onClick={handleDismiss} aria-label="Cerrar aviso de oferta" className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-tinta/55 transition-colors hover:bg-crema hover:text-tinta focus:outline-none focus-visible:ring-2 focus-visible:ring-rosa/40" data-id="element-131" data-mp="e0j5v6gev5go">
                <XIcon className="h-4 w-4" strokeWidth={2} data-id="element-132" data-mp="1zgqjbbfqij8d" />
              </button>
            </div>
          </motion.aside>}
      </AnimatePresence>
    </div>;
}
function scanProductCatalog(): ProductOffer[] {
  const structuredOffers = scanJsonLdOffers();
  if (structuredOffers.length > 0) return structuredOffers;
  const priceSection = document.getElementById('precio');
  const pageThumbnail = priceSection?.querySelector<HTMLImageElement>('img')?.src || document.querySelector<HTMLImageElement>('#inicio img')?.src || FALLBACK_OFFER.thumbnailUrl;
  if (!priceSection) return [FALLBACK_OFFER];
  const prices = findPrices(priceSection.textContent ?? '');
  const activePrice = prices.length > 0 ? Math.min(...prices) : FALLBACK_OFFER.priceNumber;
  const priceText = activePrice === FALLBACK_OFFER.priceNumber ? '$17 USD' : `$${formatPrice(activePrice)} USD`;
  const productTitle = document.querySelector<HTMLElement>('[data-product-title]')?.innerText.trim() || document.querySelector<HTMLElement>('#inicio h1')?.innerText.trim() || FALLBACK_OFFER.title;
  const editionLabel = document.body.textContent?.match(/programa guiado de 30 días/i) ? 'Programa guiado de 30 días' : FALLBACK_OFFER.editionLabel;
  return [{
    title: productTitle || FALLBACK_OFFER.title,
    editionLabel,
    priceNumber: activePrice,
    priceText,
    thumbnailUrl: pageThumbnail
  }];
}
function scanJsonLdOffers(): ProductOffer[] {
  const offers: ProductOffer[] = [];
  const scripts = document.querySelectorAll<HTMLScriptElement>('script[type="application/ld+json"]');
  scripts.forEach(script => {
    try {
      const data: unknown = JSON.parse(script.textContent ?? '');
      findProducts(data).forEach(product => {
        const productRecord = product as Record<string, unknown>;
        const offer = asOffer(productRecord.offers);
        const priceNumber = toNumber(offer?.price);
        if (!priceNumber) return;
        const currency = typeof offer?.priceCurrency === 'string' ? offer.priceCurrency : 'USD';
        offers.push({
          title: stringValue(productRecord.name) || FALLBACK_OFFER.title,
          editionLabel: stringValue(productRecord.category) || FALLBACK_OFFER.editionLabel,
          priceNumber,
          priceText: formatCurrency(priceNumber, currency),
          thumbnailUrl: imageValue(productRecord.image) || FALLBACK_OFFER.thumbnailUrl
        });
      });
    } catch {
      // Invalid or unrelated structured data should not prevent DOM detection.
    }
  });
  return offers;
}
function findProducts(value: unknown): unknown[] {
  if (Array.isArray(value)) return value.flatMap(findProducts);
  if (!value || typeof value !== 'object') return [];
  const record = value as Record<string, unknown>;
  const type = record['@type'];
  const isProduct = type === 'Product' || Array.isArray(type) && type.includes('Product');
  const nested = Object.values(record).flatMap(findProducts);
  return isProduct ? [record, ...nested] : nested;
}
function asOffer(value: unknown): Record<string, unknown> | undefined {
  const offer = Array.isArray(value) ? value[0] : value;
  return offer && typeof offer === 'object' ? offer as Record<string, unknown> : undefined;
}
function findPrices(text: string): number[] {
  const patterns = [/\$\s?(\d+(?:[.,]\d{1,2})?)/g, /(?:USD|US\$)\s?(\d+(?:[.,]\d{1,2})?)/gi, /(\d+(?:[.,]\d{1,2})?)\s?(?:USD|US\$)/gi];
  const prices = patterns.flatMap(pattern => {
    const matches = Array.from(text.matchAll(pattern));
    return matches.map(match => toNumber(match[1])).filter((price): price is number => price !== undefined);
  });
  return [...new Set(prices)];
}
function selectOffer(catalog: ProductOffer[]): ProductOffer {
  return catalog[Math.floor(Math.random() * catalog.length)] ?? FALLBACK_OFFER;
}
function toNumber(value: unknown): number | undefined {
  const parsed = Number(String(value).replace(',', '.').replace(/[^0-9.]/g, ''));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}
function stringValue(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}
function imageValue(value: unknown): string | undefined {
  if (typeof value === 'string') return value;
  if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
  if (value && typeof value === 'object' && 'url' in value) return stringValue(value.url);
  return undefined;
}
function formatCurrency(price: number, currency: string): string {
  return `$${formatPrice(price)} ${currency}`;
}
function formatPrice(price: number): string {
  return Number.isInteger(price) ? String(price) : price.toFixed(2);
}
function randomBetween(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}